import React, { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useInView } from "framer-motion";
import { LANDING_PATHS } from "@/lib/landingPaths";
import ChoiceMagneticButton from "../shared/ChoiceMagneticButton.jsx";
import ChoiceRevealOnScroll from "../shared/ChoiceRevealOnScroll.jsx";
import { fadeUpVariants, staggerContainer } from "../shared/animations.js";
import { CheckIcon, ArrowRight, RoofingIcon, BrokerIcon } from "../shared/icons.jsx";

const ROOFING_VIDEO_ID = "dYvjhIAzByo";
const BROKER_VIDEO_ID = "-BagyWtZRO4";

const PlugPlaySection = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  return (
    <section id="plug-play" className="section-padding mesh-gradient" data-testid="plug-play-section" ref={ref}>
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <ChoiceRevealOnScroll className="text-center mb-16">
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold mb-6 leading-tight" data-testid="plug-play-headline">
            <span className="text-white">Plug&Play:</span>{" "}
            <span className="gradient-text">AI Digital Employees</span>
          </h2>
          <p className="text-lg text-gray-400 font-body max-w-3xl mx-auto mb-4" data-testid="plug-play-intro">
            Pre-configured digital employees, live in 72 hours.
          </p>
          <div
            className="inline-flex flex-wrap items-center justify-center gap-2 sm:gap-3 px-4 py-2 rounded-full border border-white/10 bg-white/[0.03]"
            data-testid="plug-play-thesis"
          >
            <span className="text-gray-400 text-sm font-mono">Same ad spend</span>
            <span className="text-gray-600">·</span>
            <span className="text-gray-400 text-sm font-mono">0 new hires</span>
            <span className="text-gray-600">·</span>
            <span className="text-white font-semibold text-sm font-mono">up to 2x qualified appointments</span>
          </div>
        </ChoiceRevealOnScroll>

        <motion.div
          className="grid lg:grid-cols-2 gap-8"
          variants={staggerContainer}
          initial="hidden"
          animate={isInView ? "visible" : "hidden"}
        >
          <motion.div
            className="bento-card p-8 sm:p-10 highlight-border relative group"
            variants={fadeUpVariants}
            data-testid="path-card-roofing"
          >
            <div className="flex items-center justify-between mb-8">
              <span className="badge-mono text-orange-400 bg-orange-500/10 border border-orange-500/20 px-3 py-1.5 rounded-full">
                01 · ROOFING INTERCEPTOR
              </span>
              <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-amber-500 to-orange-500 flex items-center justify-center group-hover:scale-110 transition-transform duration-300 icon-container">
                <RoofingIcon />
              </div>
            </div>

            <h3 className="text-3xl sm:text-4xl font-bold mb-3 leading-tight">
              <span className="gradient-text">AI Roofing Interceptor</span>
            </h3>

            <p className="text-orange-400 font-mono text-sm italic mb-6">
              &quot;A Specialized Pre-Site Auditor for the Roofing Sector&quot;
            </p>

            <p className="text-gray-400 font-body leading-relaxed mb-8">
              A deterministic solution for roofing contractors to capture high-priority leads before competitors even see the email notification. Built to completely eliminate delayed response times for off-hour inspections.
            </p>

            <div
              className="w-full aspect-video rounded-xl overflow-hidden mb-8 border border-orange-500/20 bg-black/50"
              data-testid="path-card-roofing-video"
            >
              <iframe
                width="100%"
                height="100%"
                src={`https://www.youtube.com/embed/${ROOFING_VIDEO_ID}?rel=0&modestbranding=1`}
                title="AI Roofing Interceptor demo"
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                referrerPolicy="strict-origin-when-cross-origin"
                allowFullScreen
              />
            </div>

            <ul className="space-y-4 mb-10">
              <li className="flex items-start space-x-3">
                <span className="text-orange-400 mt-0.5 flex-shrink-0"><CheckIcon /></span>
                <span className="text-gray-300 font-body text-sm sm:text-base">
                  <span className="text-white font-semibold">9-Second Interception:</span> Captures inbound requests, leak emergencies, and storm damage reports in under 9 seconds, 24/7.
                </span>
              </li>
              <li className="flex items-start space-x-3">
                <span className="text-orange-400 mt-0.5 flex-shrink-0"><CheckIcon /></span>
                <span className="text-gray-300 font-body text-sm sm:text-base">
                  <span className="text-white font-semibold">Automated Pre-Site Audits:</span> Runs a strict qualification protocol to vet leads, identify the exact problem, and filter out out-of-service inquiries.
                </span>
              </li>
              <li className="flex items-start space-x-3">
                <span className="text-orange-400 mt-0.5 flex-shrink-0"><CheckIcon /></span>
                <span className="text-gray-300 font-body text-sm sm:text-base">
                  <span className="text-white font-semibold">Calendar Lock-Down:</span> Independently secures high-priority roof inspection slots directly into your estimator&apos;s schedule, entirely outside of normal business hours.
                </span>
              </li>
            </ul>

            <Link
              to={LANDING_PATHS.roofing}
              className="block"
              data-testid="path-cta-roofing-link"
            >
              <ChoiceMagneticButton
                className="w-full bg-gradient-to-r from-amber-600 via-orange-500 to-orange-500 px-8 py-4 rounded-2xl font-semibold text-base sm:text-lg flex items-center justify-center space-x-2"
                data-testid="path-cta-roofing"
              >
                <span>Deploy Your Roofing Interceptor</span>
                <ArrowRight />
              </ChoiceMagneticButton>
            </Link>

            <p className="text-xs text-gray-500 font-mono text-center mt-4" data-testid="path-pricing-roofing">
              14-Day Free Trial · No credit card required
              <br />
              $99/month after trial · Launch in 72 Hours
            </p>
          </motion.div>

          <motion.div
            className="bento-card p-8 sm:p-10 highlight-border relative group"
            variants={fadeUpVariants}
            data-testid="path-card-broker"
          >
            <div className="flex items-center justify-between mb-8">
              <span className="badge-mono text-teal-400 bg-teal-500/10 border border-teal-500/20 px-3 py-1.5 rounded-full">
                02 · AI BROKER
              </span>
              <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-teal-500 to-emerald-500 flex items-center justify-center group-hover:scale-110 transition-transform duration-300 icon-container">
                <BrokerIcon />
              </div>
            </div>

            <h3 className="text-3xl sm:text-4xl font-bold mb-3 leading-tight">
              <span className="gradient-text">AI Broker</span>
            </h3>

            <p className="text-teal-400 font-mono text-sm italic mb-6">
              &quot;A Specialized AI Closer for Real Estate&quot;
            </p>

            <p className="text-gray-400 font-body leading-relaxed mb-8">
              A turnkey solution for real estate agencies. While your agents are at showings or asleep, your Digital Broker captures and qualifies every inbound lead.
            </p>

            <div
              className="w-full aspect-video rounded-xl overflow-hidden mb-8 border border-teal-500/20 bg-black/50"
              data-testid="path-card-broker-video"
            >
              <iframe
                width="100%"
                height="100%"
                src={`https://www.youtube.com/embed/${BROKER_VIDEO_ID}?rel=0&modestbranding=1`}
                title="AI Broker demo"
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                referrerPolicy="strict-origin-when-cross-origin"
                allowFullScreen
              />
            </div>

            <ul className="space-y-4 mb-10">
              <li className="flex items-start space-x-3">
                <span className="text-teal-400 mt-0.5 flex-shrink-0"><CheckIcon /></span>
                <span className="text-gray-300 font-body text-sm sm:text-base">
                  <span className="text-white font-semibold">Turnkey Launch:</span> Full integration in 72 hours. Trained specifically for the high-friction real estate market.
                </span>
              </li>
              <li className="flex items-start space-x-3">
                <span className="text-teal-400 mt-0.5 flex-shrink-0"><CheckIcon /></span>
                <span className="text-gray-300 font-body text-sm sm:text-base">
                  <span className="text-white font-semibold">Stop Lead Leakage:</span> Reply in under 9 seconds, 24/7. Immediate and accurate qualification of sellers, buyers, and tenants.
                </span>
              </li>
              <li className="flex items-start space-x-3">
                <span className="text-teal-400 mt-0.5 flex-shrink-0"><CheckIcon /></span>
                <span className="text-gray-300 font-body text-sm sm:text-base">
                  <span className="text-white font-semibold">Autonomous Appraisals:</span> The digital employee independently books property showings and appraisal meetings directly into your human broker&apos;s calendar.
                </span>
              </li>
            </ul>

            <Link
              to={LANDING_PATHS.realEstate}
              className="block"
              data-testid="path-cta-broker-link"
            >
              <ChoiceMagneticButton
                className="w-full bg-gradient-to-r from-teal-500 via-emerald-500 to-teal-500 px-8 py-4 rounded-2xl font-semibold text-base sm:text-lg flex items-center justify-center space-x-2"
                data-testid="path-cta-broker"
              >
                <span>Deploy Your AI Broker</span>
                <ArrowRight />
              </ChoiceMagneticButton>
            </Link>

            <p className="text-xs text-gray-500 font-mono text-center mt-4">
              $399 Setup · Launch in 72 Hours
            </p>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};

export default PlugPlaySection;
